import { useEffect, useRef, useState } from "react"
import { Panel, severityClasses } from "../Panel"
import type { DisruptionItem } from "../dashboard-types"
import { useChat, useRealtimeStore, useRegion } from "../store"

/** Read the OS reduced-motion preference once (cheap, mount-time). */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

const INCIDENT_FLAG_TTL_MS = 4_000

/* Watches the realtime store's `incidentPulse` counter. When it advances (a
   `*.incident_created` envelope arrived over SSE), surface a short-lived flag
   at the top of the feed. The poll remains the source of the actual list — the
   flag is purely a "fresh signal landed" liveness cue and auto-clears. */
function useIncidentFlag(): boolean {
  const incidentPulse = useRealtimeStore((s) => s.incidentPulse)
  const lastSeen = useRef(incidentPulse)
  const [flagged, setFlagged] = useState(false)

  useEffect(() => {
    if (incidentPulse === lastSeen.current) return
    lastSeen.current = incidentPulse
    setFlagged(true)
    const t = window.setTimeout(() => setFlagged(false), INCIDENT_FLAG_TTL_MS)
    return () => window.clearTimeout(t)
  }, [incidentPulse])

  return flagged
}

const SEV_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, critical: 3 }

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export function DisruptionFeed({ items, demo }: { items: DisruptionItem[]; demo?: boolean }) {
  const openRegion = useRegion((s) => s.openRegion)
  const incidentFlag = useIncidentFlag()
  const reducedMotion = prefersReducedMotion()

  // LLM set_panel_filter support (panel_id "intel"): `severity` keeps items at
  // or above the given level; `region` keeps items tagged with the region.
  const filters = useChat((s) => s.panelFilters["intel"])
  const clearPanelFilter = useChat((s) => s.clearPanelFilter)
  const sevFilter = filters?.["severity"]?.toLowerCase()
  const regionFilter = filters?.["region"]?.toUpperCase()

  const visible = items.filter((it) => {
    if (sevFilter && SEV_RANK[sevFilter] != null) {
      if ((SEV_RANK[it.severity] ?? 0) < (SEV_RANK[sevFilter] ?? 0)) return false
    }
    if (regionFilter && !it.regions.some((r) => r.toUpperCase() === regionFilter)) return false
    return true
  })

  const activeFilters = Object.entries(filters ?? {})

  return (
    <Panel title="Disruption Intel" live demo={demo} count={visible.length} helpText="AI-synthesized disruption incidents from news, weather, and port feeds">
      {incidentFlag && (
        <div
          role="status"
          className="px-3 py-1.5 flex items-center gap-1.5 border-b border-emerald-500/30 bg-emerald-500/10"
          style={
            reducedMotion
              ? undefined
              : { animation: "sl-incident-flag 320ms ease-out" }
          }
        >
          {!reducedMotion && (
            <style>{
              "@keyframes sl-incident-flag{from{opacity:0;transform:translateY(-3px)}" +
              "to{opacity:1;transform:translateY(0)}}"
            }</style>
          )}
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
          <span className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold">
            new incident · live signal
          </span>
          <span className="text-[9px] text-emerald-400/70">refreshing on next poll</span>
        </div>
      )}
      {activeFilters.length > 0 && (
        <div className="px-3 py-1.5 flex flex-wrap items-center gap-1 border-b border-[color:var(--border)]">
          <span className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">
            filters
          </span>
          {activeFilters.map(([key, value]) => (
            <button
              key={key}
              type="button"
              onClick={() => clearPanelFilter("intel", key)}
              title={`Clear ${key} filter`}
              className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[color:var(--accent)]/15 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/30"
            >
              {key}: {value} ×
            </button>
          ))}
        </div>
      )}
      <div>
        {visible.length === 0 && (
          <div className="px-3 py-6 text-center text-[11px] text-[color:var(--text-dim)] uppercase tracking-wider">
            no incidents match filters
          </div>
        )}
        {visible.map((it) => {
          const sv = severityClasses(it.severity)
          return (
            <article key={it.id} className="incident-row" data-severity={it.severity}>
              <div className="incident-meta"><span className={`incident-severity ${sv.text}`}><i />{it.severity}</span><time dateTime={it.started_at}>{relTime(it.started_at)}</time></div>
              <h4>{it.title}</h4>
              <p>{it.summary}</p>
              <div className="incident-tags">{it.modes.slice(0, 2).map(m => <span key={m}>{m}</span>)}{it.regions.slice(0, 2).map(r => <button key={r} type="button" onClick={() => openRegion(r)} title={`Explore ${r}`}>{r.replaceAll("_", " ")} ↗</button>)}</div>
              <div className="incident-source">{it.source} <span>·</span> {Math.round(it.confidence * 100)}% confidence</div>
            </article>
          )
        })}
      </div>
    </Panel>
  )
}
